import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { sankey, sankeyLinkHorizontal, sankeyLeft } from 'd3-sankey'
import type { ProposalResponse } from '@/types/waterfall'
import { MEMBERS, MEMBER_COLORS, TIER_LABELS } from '@/types/waterfall'
import type { TierBreakdown } from '@/types/waterfall'
import { tierColumnTotal } from '@/utils/waterfallGrid'

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
})

const ASPECT_RATIO = 320 / 720
const MIN_WIDTH = 280
const DEFAULT_WIDTH = 720

interface SankeyNode {
  name: string
  color?: string
}

interface SankeyNodeExtra extends SankeyNode {
  nodeId: number
}

interface SankeyLink {
  source: number
  target: number
  value: number
}

const TIER_ORDER: (keyof TierBreakdown)[] = ['t1', 'hp', 's3a', 's3b', 's4a', 's4b']
const TIER_NAMES = new Set<string>(Object.values(TIER_LABELS))

function isTierNode(name: string): boolean {
  return TIER_NAMES.has(name)
}

function compareSankeyNodes(
  a: SankeyNodeExtra & { value?: number },
  b: SankeyNodeExtra & { value?: number },
): number {
  const tierA = isTierNode(a.name)
  const tierB = isTierNode(b.name)
  if (tierA && tierB) {
    return a.name.localeCompare(b.name)
  }
  // Keep member column in stable insertion order so tier→member streams
  // stack within each tier instead of globally by total value.
  return a.nodeId - b.nodeId
}

function compareSankeyLinks(
  a: SankeyLink & { source: SankeyNodeExtra; target: SankeyNodeExtra; value: number },
  b: SankeyLink & { source: SankeyNodeExtra; target: SankeyNodeExtra; value: number },
): number {
  if (a.source.name === 'Distribution') {
    return a.target.name.localeCompare(b.target.name)
  }
  return b.value - a.value
}

type LayoutLink = SankeyLink & {
  source: SankeyNodeExtra
  target: SankeyNodeExtra
  width?: number
}

function linkTooltipText(link: LayoutLink): string {
  const { source, target, value } = link
  if (source.name === 'Distribution') {
    return `${target.name}: ${fmt.format(value)}`
  }
  return `${source.name} → ${target.name}: ${fmt.format(value)}`
}

function buildGraph(row: TierBreakdown, total: number): { nodes: SankeyNodeExtra[]; links: SankeyLink[] } {
  const nodes: SankeyNodeExtra[] = [{ name: 'Distribution', nodeId: 0 }]
  const links: SankeyLink[] = []

  TIER_ORDER.forEach((tierKey) => {
    const tierTotal = tierColumnTotal(row[tierKey])
    if (tierTotal <= 0) return
    const tierIndex = nodes.length
    nodes.push({ name: TIER_LABELS[tierKey], nodeId: tierIndex })
    links.push({ source: 0, target: tierIndex, value: tierTotal })

    MEMBERS.forEach((m) => {
      const val = row[tierKey][m]
      if (val <= 0) return
      const existing = nodes.findIndex((n) => n.name === m)
      const targetIndex =
        existing >= 0
          ? existing
          : (nodes.push({ name: m, color: MEMBER_COLORS[m], nodeId: nodes.length }) - 1)
      links.push({ source: tierIndex, target: targetIndex, value: val })
    })
  })

  if (nodes.length === 1 && total > 0) {
    nodes.push({ name: 'Unallocated', nodeId: 1 })
    links.push({ source: 0, target: 1, value: total })
  }

  return { nodes, links }
}

export default function WaterfallSankey({
  result,
  embedded = false,
}: {
  result: ProposalResponse
  embedded?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [hover, setHover] = useState<{ x: number; y: number; text: string } | null>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const updateWidth = () => {
      setWidth(Math.max(el.clientWidth, MIN_WIDTH))
    }

    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const height = Math.max(Math.round(width * ASPECT_RATIO), 180)

  const layout = useMemo(() => {
    const graph = buildGraph(result.proposal.row_for_sheet, result.proposal.total)
    const generator = sankey<SankeyNodeExtra, SankeyLink>()
      .nodeId((d) => d.nodeId)
      .nodeWidth(14)
      .nodePadding(12)
      .extent([
        [8, 8],
        [width - 8, height - 8],
      ])
      .nodeAlign(sankeyLeft)
      .iterations(0)
      .nodeSort((a, b) =>
        compareSankeyNodes(
          a as SankeyNodeExtra & { value?: number },
          b as SankeyNodeExtra & { value?: number },
        ),
      )
      .linkSort((a, b) =>
        compareSankeyLinks(
          a as SankeyLink & { source: SankeyNodeExtra; target: SankeyNodeExtra; value: number },
          b as SankeyLink & { source: SankeyNodeExtra; target: SankeyNodeExtra; value: number },
        ),
      )

    return generator({
      nodes: graph.nodes.map((n) => ({ ...n })),
      links: graph.links.map((l) => ({ ...l })),
    })
  }, [height, result, width])

  const pathGen = sankeyLinkHorizontal()

  const updateHover = (event: MouseEvent<SVGPathElement>, text: string) => {
    const chart = chartRef.current
    if (!chart) return
    const rect = chart.getBoundingClientRect()
    setHover({
      x: event.clientX - rect.left + 12,
      y: event.clientY - rect.top + 12,
      text,
    })
  }

  return (
    <div
      className={
        embedded
          ? 'min-w-0'
          : 'overflow-x-auto rounded-lg border border-gray-200 bg-white p-4'
      }
    >
      <p
        className={
          embedded
            ? 'mb-2 text-xs font-medium text-gray-700'
            : 'mb-2 text-sm font-medium text-gray-900'
        }
      >
        Cash flow
      </p>
      <div ref={containerRef} className={embedded ? 'w-full min-w-0' : 'w-full max-w-3xl'}>
        <div ref={chartRef} className="relative">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="block w-full"
            role="img"
            aria-label="Distribution waterfall sankey"
          >
            <title>Distribution waterfall sankey</title>
            {layout.links.map((link, i) => {
              const d = pathGen(link)
              if (!d) return null
              const layoutLink = link as LayoutLink
              const source = layoutLink.source
              const target = layoutLink.target
              const strokeWidth = Math.max(1, layoutLink.width ?? 1)
              const tooltip = linkTooltipText(layoutLink)
              return (
                <g key={i}>
                  <path
                    d={d}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={Math.max(strokeWidth, 14)}
                    className="cursor-pointer"
                    onMouseEnter={(event) => updateHover(event, tooltip)}
                    onMouseMove={(event) => updateHover(event, tooltip)}
                    onMouseLeave={() => setHover(null)}
                  />
                  <path
                    d={d}
                    fill="none"
                    stroke={target.color ?? source.color ?? '#94a3b8'}
                    strokeOpacity={0.35}
                    strokeWidth={strokeWidth}
                    className="pointer-events-none"
                  />
                </g>
              )
            })}
            {layout.nodes.map((node, i) => (
              <g key={i} transform={`translate(${node.x0 ?? 0},${node.y0 ?? 0})`}>
                <rect
                  width={Math.max(1, (node.x1 ?? 0) - (node.x0 ?? 0))}
                  height={Math.max(1, (node.y1 ?? 0) - (node.y0 ?? 0))}
                  fill={node.color ?? '#64748b'}
                  rx={2}
                />
                <text
                  x={-6}
                  y={((node.y1 ?? 0) - (node.y0 ?? 0)) / 2}
                  dy="0.35em"
                  textAnchor="end"
                  className="fill-gray-700 text-[10px] pointer-events-none"
                >
                  {node.name}
                </text>
              </g>
            ))}
          </svg>
          {hover ? (
            <div
              className="pointer-events-none absolute z-10 max-w-xs rounded-md border border-gray-200 bg-white px-2.5 py-1.5 shadow-md"
              style={{ left: hover.x, top: hover.y }}
            >
              <p className="text-xs font-medium text-gray-900">{hover.text}</p>
            </div>
          ) : null}
        </div>
      </div>
      {!embedded && (
        <p className="mt-2 text-xs text-gray-500">
          Link width proportional to {fmt.format(result.proposal.total)} total distribution.
        </p>
      )}
    </div>
  )
}
